import BaseService from "../../base/base_service";
import { SYSTEM_EVENTS } from "../../events/events";
import {
    DAILY_JACKPOTS_AMOUNT_THRESHOLDS_DEFAULT, DAILY_JACKPOTS_STATES
    , IJackpotToastersThresholdDetails, IToasterPerGame, TWILIGHT_JACKPOTS_AMOUNT_THRESHOLDS_DEFAULT
} from "./jackpot_toasters_consts";
import { IJackpotDataById, IJackpotData } from "../live_feeds/jackpots/jackpot_feeds_service_interface";
import { JackpotInternalId } from '../../../../common/const/jackpot_map_ids';
import _ from '../../utils/underscore';
import {TimeSpan} from "@sparkware/app-utils";

interface IJackpotToastersServiceParams {
    toasterImage: string;
    dailyJackpotAmountThresholds?: IJackpotToastersThresholdDetails[];
    twilightJackpotAmountThresholds?: IJackpotToastersThresholdDetails[];
    dailyJackpot: {
        gameTypeToPlay: number;
    },
    twilightJackpot: {
        gameTypeToPlay: number;
    }
    progressiveJackpot: {
        enabled: boolean;
    }
    toasterPerGame?: IToasterPerGame[];
}

export default class JackpotToastersService extends BaseService {
    static CLASS: string = 'JackpotToastersService';

    private _previousJackpot: {[key: number]:IJackpotData} = {};
    private _previousJackpotPerGameType = {};

    private _params: IJackpotToastersServiceParams = null;

    init(params: IJackpotToastersServiceParams) {
        super.init(params);
        this._params = params;
        this._subscribe();
    }

    private _subscribe(): void {
        this.onSystemEvent(SYSTEM_EVENTS.LIVE_FEEDS_JACKPOTS_BY_ID_UPDATED, this._stateJackpotUpdate);

        if(this._params?.progressiveJackpot.enabled && this._params?.toasterPerGame?.length > 0) {
            this.onSystemEvent(SYSTEM_EVENTS.LIVE_FEEDS_JACKPOT_AMOUNTS_BY_GAME_UPDATED, this._onJackpotUpdate);
        }

        this.onSystemEvent(SYSTEM_EVENTS.APP_PAUSED, this.clearPreviousJackpotsAmounts);
    }

    private clearPreviousJackpotsAmounts() {
        this._previousJackpotPerGameType = {};
        this._previousJackpot = {};
    }

    private _onJackpotUpdate(gamesJackpots) {
        this._params?.toasterPerGame?.forEach((toasterPerGameItem)=> {
            toasterPerGameItem?.gameTypes?.forEach((gameType)=> {
                if (this.serviceFactory.oAuth2Service().isAuthorized()) {
                    let jackpotAmount = gamesJackpots[gameType];

                    if(jackpotAmount) {
                        if(this._previousJackpotPerGameType[gameType]) {
                            this._checkThreshold(jackpotAmount, this._previousJackpotPerGameType[gameType], toasterPerGameItem?.thresholds, gameType, null, toasterPerGameItem.showGameIcon);
                        }

                        this._previousJackpotPerGameType[gameType] = jackpotAmount;
                    }
                } else {
                    // Initialize previous jackpot.
                    this._previousJackpotPerGameType[gameType] = null;
                }
            });
        });
    }

    private _getResolvedGameImagePath(gameData) {
        return this.serviceFactory.gameService().resolveGameImagePath(gameData.images && gameData.images.icon);
    }

    private _checkThreshold(jackpotAmount: number, oldJackpotAmount: number, thresholds: IJackpotToastersThresholdDetails[], gameTypeToPlay: number, titleText: string, showGameIcon: boolean = false): void {
        for (let index = 0; index < thresholds.length; index++) {
            const currentThresholdData = thresholds[index];

            if (jackpotAmount > currentThresholdData.threshold &&
                oldJackpotAmount <= currentThresholdData.threshold) {
                this._showToaster(currentThresholdData, gameTypeToPlay, currentThresholdData?.titleText || titleText, showGameIcon);
                break;
            }
        }
    }

    private _checkIfJackpotDrop(currentJackpotData: IJackpotData, jackpotID:string, titleText: string) {
        if(currentJackpotData.JackpotState === DAILY_JACKPOTS_STATES.SUPER_COLD &&
            this._previousJackpot[jackpotID] &&
            this._previousJackpot[jackpotID].JackpotState !== DAILY_JACKPOTS_STATES.SUPER_COLD) {
            this._showJackpotDropToaster(currentJackpotData, titleText);
        }
    }

    private _showJackpotDropToaster(toasterData: IJackpotData, titleText: string) {
        this.serviceFactory.toasterProxyService().showStandardToast({
            id: 'DAILY_JP_DROP_ALERT',
            toastOptions: { timeout: TimeSpan.fromSeconds(15) },
            contentProps: {
                title: this.serviceFactory.languageService().translation(titleText, {jackpotAmount: this.serviceFactory.currencyService().formatMoney(toasterData.LastWinAmount)}),
                message: this.serviceFactory.languageService().translation('jackpot_drop_toaster_text'),
                image: this._params.toasterImage,
                button: {
                    text: this.serviceFactory.languageService().translation('game_item_play_btn'),
                    onClick: (closeToast) => {
                        this.serviceFactory.windowService().redirect('/jackpots/');
                        closeToast();
                    }
                }
            }
        });
    }

    private async _showToaster(toasterData: IJackpotToastersThresholdDetails, gameTypeToPlay: number, titleText: string, showGameIcon:boolean = false) {
        const gameInfo = await this.serviceFactory.gamesInformationService().getGamesInfo([gameTypeToPlay]);
        if(!gameInfo || !gameInfo[gameTypeToPlay]) {
            this.logger.warn(`gameInfo for the toaster button not found. gameType: ${gameTypeToPlay}`);
            return;
        }

        this.serviceFactory.toasterProxyService().showStandardToast({
            id: toasterData.toasterName,
            toastOptions: {
                timeout: TimeSpan.fromSeconds(15)
            },
            contentProps: {
                title: this.serviceFactory.languageService().translation(titleText, {gameName: gameInfo[gameTypeToPlay].title}),
                message: this.serviceFactory.languageService().translation(toasterData.contentText, {
                    currencySymbol: this.serviceFactory.currencyService().getCurrencySymbol(),
                    jackpotThreshold: _.formatNumberToBytesNotation(toasterData.threshold / 100, 2)
                }),
                image: showGameIcon ? this._getResolvedGameImagePath(gameInfo[gameTypeToPlay]) : this._params.toasterImage,
                button: {
                    text: this.serviceFactory.languageService().translation('game_item_play_btn'),
                    onClick: (closeToast) => {
                        this.serviceFactory.gameRunnerProxyService().openRealGameFromSdkRequest(gameTypeToPlay);
                        closeToast();
                    }
                }
            }
        });
    }

    private _stateJackpotToaster(stateJackpot: IJackpotData, thresholdReachedTitleText:string, thresholds: IJackpotToastersThresholdDetails[], gameTypeToPlay: number, jackpotID: string, dropTitleText: string) {
        // if not authorized or cold state- should not show toaster
        if (stateJackpot && this.serviceFactory.oAuth2Service().isAuthorized()) {
            // we don't want notification to show when user refreshed page and we don't have previous value
            // and even after some JP updates we want to show it only after next threshold is reached
            if(stateJackpot.JackpotState !== DAILY_JACKPOTS_STATES.SUPER_COLD && this._previousJackpot[jackpotID] && stateJackpot) {
                this._checkThreshold(stateJackpot?.AmountInCents,
                    this._previousJackpot[jackpotID]?.AmountInCents, // can be undefined
                    thresholds,
                    gameTypeToPlay,
                    thresholdReachedTitleText,
                    false
                );
            } else {
                this._checkIfJackpotDrop(stateJackpot, jackpotID, dropTitleText);
            }

            this._previousJackpot[jackpotID] = stateJackpot;
        }
    }

    private _stateJackpotUpdate(jackpotsWithState: Record<number, IJackpotDataById>): void {
        if(jackpotsWithState[JackpotInternalId.Daily]) {
            this._stateJackpotToaster(jackpotsWithState[JackpotInternalId.Daily].Jackpot,
                'jackpot_threshold_toaster_title',
                this._params?.dailyJackpotAmountThresholds || DAILY_JACKPOTS_AMOUNT_THRESHOLDS_DEFAULT,
                    this._params?.dailyJackpot.gameTypeToPlay,
                    '8000',
                    'jackpot_drop_toaster_title'
                );
        }

        if(jackpotsWithState[JackpotInternalId.Twilight]) {
            this._stateJackpotToaster(jackpotsWithState[JackpotInternalId.Twilight].Jackpot,
                'twilight_jackpot_threshold_toaster_title',
                this._params?.twilightJackpotAmountThresholds || TWILIGHT_JACKPOTS_AMOUNT_THRESHOLDS_DEFAULT,
                this._params?.twilightJackpot.gameTypeToPlay,
                '8001',
                'twilight_jackpot_drop_toaster_title'
            );
        }
    }
}
